// Theme-aware design tokens for the customer embeds — inline styles only.
// The embed renders inside a host iframe/page we do not style; every value
// here is applied via React inline `style` props off a TokenSet.
//
// ⚠️ AMENDED 2026-09-30 (embed design-system parity)
//   ORIGINAL: a hand-picked palette and type scale, chosen independently of
//             the Aldelo design system (primary #1D59FF, 12/14/16/20/24/32
//             type scale, 44px controls everywhere, rgba-tinted input fill).
//   CURRENT:  a projection of the vendored aldelo-cloud.tokens.json. Every
//             value comes from ds-tokens.generated.ts (emitted by
//             scripts/gen-embed-tokens.mjs), and COLOR_SOURCES below names
//             the design-system token behind each colour key. The parity
//             test (tokens.test.ts) pins each key to the JSON, so the embed
//             can no longer drift from the design system silently.
//   WHY:      the embed must read as one product with the portal. It still
//             cannot import the portal stylesheet (no shadow DOM, no host
//             CSS injection), which is why the values are generated into
//             inline-style tokens rather than shared as CSS.

import { DS_COLOR_LIGHT, DS_COLOR_DARK, DS_SCALAR } from "./ds-tokens.generated";

export type Pointer = "fine" | "coarse";

// Opacity of the recurring-charge tint. 0.08, not the 0.14 it used to be: the
// design-system data colour (#7657d6) on a 14% tint of itself measures 4.23:1
// on a white card, under the 4.5:1 text floor; 8% measures 4.62:1.
const RECURRING_TINT_ALPHA = 0.08;

export interface ThemeColors {
  primary: string; primaryHover: string; primaryTint: string; onAction: string;
  text: string; textSecondary: string; textStrong: string; placeholder: string;
  link: string; linkHover: string; focus: string;
  error: string; errorBg: string; dangerHover: string; onDanger: string; dangerSoft: string;
  successGreen: string; successBg: string; successSoft: string;
  warning: string; warningSoft: string; info: string; infoSoft: string;
  neutral: string; neutralSoft: string;
  white: string; inputBg: string;
  border: string; borderSubtle: string; borderFocus: string;
  disabled: string; disabledBg: string;
  // surfaces
  surface: string; surfaceSunken: string; surfaceHover: string; background: string;
  cardBg: string; cardBorder: string; cardShadow: string;
  scrim: string; overlayShadow: string;
  // marketplace
  chargeOneTime: string; chargeRecurring: string; chargeRecurringTint: string; priceOriginal: string;
  // stepper
  stepperActive: string; stepperComplete: string; stepperPending: string;
}

export interface TokenSet {
  fontFamily: string; fontFamilyMono: string;
  borderRadiusSm: number; borderRadiusControl: number; borderRadius: number; borderRadiusLg: number;
  inputPadding: number; inputFontSize: number; buttonHeight: number;
  spacing: { xs: number; sm: number; md: number; lg: number; xl: number };
  fontSize: { xs: number; sm: number; md: number; lg: number; xl: number; xxl: number };
  fontWeight: { regular: number; medium: number; strong: number };
  lineHeight: { tight: number; normal: number; relaxed: number };
  size: {
    control: number; touchTarget: number; formMax: number;
    dialogSmall: number; dialogMedium: number; dialogLarge: number;
    /** Container width below which a table stacks into a list. */
    tableStackBelow: number;
  };
  /** Pointer the geometry was resolved for; coarse is the touch-safe default. */
  pointer: Pointer;
  colors: ThemeColors;
}

/**
 * Where each colour key comes from. A string is a design-system token path;
 * `literal` is a value that is not a design-system token; `alpha` is a
 * design-system colour at a fixed opacity (the design system has no tint
 * token for it). The design system has no neutral status pair, so `neutral`
 * and `neutralSoft` reuse its muted text and muted surface.
 */
export const COLOR_SOURCES: Record<keyof ThemeColors, string | { literal: string } | { alpha: [string, number] }> = {
  primary: "color.action", primaryHover: "color.action-hover", primaryTint: "color.action-soft", onAction: "color.on-action",
  text: "color.text", textSecondary: "color.text-muted", textStrong: "color.text-strong", placeholder: "color.text-disabled",
  link: "color.link", linkHover: "color.link-hover", focus: "color.focus",
  error: "color.danger", errorBg: "color.danger-soft", dangerHover: "color.danger-hover", onDanger: "color.on-danger", dangerSoft: "color.danger-soft",
  successGreen: "color.success", successBg: "color.success-soft", successSoft: "color.success-soft",
  warning: "color.warning", warningSoft: "color.warning-soft", info: "color.info", infoSoft: "color.info-soft",
  neutral: "color.text-muted", neutralSoft: "color.surface-muted",
  white: { literal: "#FFFFFF" }, inputBg: "component.field.background",
  border: "color.control-border", borderSubtle: "color.border", borderFocus: "color.focus",
  disabled: "color.text-disabled", disabledBg: "color.surface-muted",
  surface: "color.surface", surfaceSunken: "color.surface-muted", surfaceHover: "color.surface-hover", background: "color.background",
  cardBg: "component.panel.background", cardBorder: "component.panel.border", cardShadow: "shadow.panel",
  scrim: "color.scrim", overlayShadow: "shadow.overlay",
  chargeOneTime: "color.action", chargeRecurring: "color.data-2", chargeRecurringTint: { alpha: ["color.data-2", RECURRING_TINT_ALPHA] },
  priceOriginal: "color.text-muted",
  stepperActive: "color.action", stepperComplete: "color.success", stepperPending: "color.border-strong",
};

const HEX_COLOR = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;

function alphaOf(hex: string, alpha: number): string {
  const m = HEX_COLOR.exec(hex);
  if (!m) throw new Error(`embed tokens: cannot apply alpha to non-hex colour ${hex}`);
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
}

function colorsFor(source: Readonly<Record<string, string>>): ThemeColors {
  const out: Record<string, string> = {};
  for (const [key, src] of Object.entries(COLOR_SOURCES)) {
    if (typeof src === "string") out[key] = source[src];
    else if ("literal" in src) out[key] = src.literal;
    else out[key] = alphaOf(source[src.alpha[0]], src.alpha[1]);
  }
  return out as unknown as ThemeColors;
}

const px = (path: keyof typeof DS_SCALAR): number => DS_SCALAR[path] as number;

/**
 * Token set for a theme and pointer. Colours swap per theme; control height
 * and input font size swap per pointer (44px controls and 16px inputs on
 * touch, which also stops iOS zooming on focus; the DS 38px / 14px on a fine
 * pointer).
 */
export function tokensFor(theme: "light" | "dark", pointer: Pointer): TokenSet {
  const coarse = pointer === "coarse";
  return {
    fontFamily: DS_SCALAR["font.family.body"],
    fontFamilyMono: DS_SCALAR["font.family.mono"],
    borderRadiusSm: px("radius.sm"),
    borderRadiusControl: px("radius.control"),
    borderRadius: px("radius.md"),
    borderRadiusLg: px("radius.xl"),
    inputPadding: px("space.300"),
    inputFontSize: coarse ? px("font.size.input-touch") : px("font.size.body-large"),
    buttonHeight: coarse ? px("size.touch-target") : px("size.control"),
    spacing: { xs: px("space.100"), sm: px("space.200"), md: px("space.400"), lg: px("space.600"), xl: px("space.800") },
    fontSize: {
      xs: px("font.size.label"), sm: px("font.size.body"), md: px("font.size.body-large"),
      lg: px("font.size.heading-small"), xl: px("font.size.display-small"), xxl: px("font.size.display"),
    },
    fontWeight: { regular: px("font.weight.regular"), medium: px("font.weight.medium"), strong: px("font.weight.strong") },
    // `relaxed` is not a design-system token; kept for the few long-form blocks that used it.
    lineHeight: { tight: px("font.line-height.heading"), normal: px("font.line-height.body"), relaxed: 1.75 },
    size: {
      control: px("size.control"), touchTarget: px("size.touch-target"), formMax: px("size.form-max"),
      dialogSmall: px("size.dialog-small"), dialogMedium: px("size.dialog-medium"), dialogLarge: px("size.dialog-large"),
      tableStackBelow: px("breakpoint.narrow"),
    },
    pointer,
    colors: colorsFor(theme === "dark" ? DS_COLOR_DARK : DS_COLOR_LIGHT),
  };
}

// Coarse is the touch-safe default, and it is what jsdom resolves to.
export const LIGHT_TOKENS: TokenSet = tokensFor("light", "coarse");
export const DARK_TOKENS: TokenSet = tokensFor("dark", "coarse");
export const LIGHT_COLORS: ThemeColors = LIGHT_TOKENS.colors;
export const DARK_COLORS: ThemeColors = DARK_TOKENS.colors;
